import PropTypes from 'prop-types';
import { Suspense } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { Spinner } from 'components/Elements/Spinner';
import { useAuth } from 'lib/auth';
import { ORDER_LIST_VIEW, PRODUCT_LIST_VIEW } from 'routes/urls';
import { EXTERNAL_ADMIN_SCOPE } from 'utils/const';
import { lazyImport } from 'utils/lazyImport';

const { Dashboard } = lazyImport(() => import('features/dashboard'), 'Dashboard');
const { UserList } = lazyImport(() => import('features/users'), 'UserList');
const { CreateUser } = lazyImport(() => import('features/users'), 'CreateUser');
const { UserDetail } = lazyImport(() => import('features/user'), 'UserDetail');
const { RequestsList } = lazyImport(() => import('features/requests'), 'RequestsList');
const { RequestDetail } = lazyImport(() => import('features/request'), 'RequestDetail');
const { InquiryList } = lazyImport(() => import('features/inquiries'), 'InquiryList');
const { InquiryDetail } = lazyImport(() => import('features/inquiry'), 'InquiryDetail');
const { ProductList } = lazyImport(() => import('features/products'), 'ProductList');
const { NonRebuiltProductDetail } = lazyImport(
  () => import('features/product'),
  'NonRebuiltProductDetail',
);
const { OrdersList } = lazyImport(() => import('features/orders'), 'OrdersList');
const { OrderDetail } = lazyImport(() => import('features/order'), 'OrderDetail');
const { PaymentsList } = lazyImport(() => import('features/payments'), 'PaymentsList');
const { PaymentDetail } = lazyImport(() => import('features/payment'), 'PaymentDetail');
const { ExhibitsList } = lazyImport(() => import('features/exhibits'), 'ExhibitsList');
const { ExhibitDetail } = lazyImport(() => import('features/exhibit'), 'ExhibitDetail');
const { AdministratorList } = lazyImport(
  () => import('features/administrators'),
  'AdministratorList',
);
const { AdministratorDetail } = lazyImport(
  () => import('features/administrator'),
  'AdministratorDetail',
);
const { CommentsList } = lazyImport(() => import('features/comments'), 'CommentsList');
const { CommentDetail } = lazyImport(() => import('features/comment'), 'CommentDetail');
const { ATRSImportErrorList } = lazyImport(
  () => import('features/atrsImportErrors'),
  'ATRSImportErrorList',
);
const { ProductsMaster } = lazyImport(() => import('features/productsMaster'), 'ProductsMaster');
const { ImportVehicleMaster } = lazyImport(
  () => import('features/importVehicleMaster'),
  'ImportVehicleMaster',
);
const { WithdrawalList } = lazyImport(() => import('features/withdrawals'), 'WithdrawalList');
const { ProductBulkUpdate } = lazyImport(
  () => import('features/productBulkUpdate'),
  'ProductBulkUpdate',
);
const { UnapprovedUserList } = lazyImport(
  () => import('features/unapprovedUsers'),
  'UnapprovedUserList',
);
const { SatisfactionRatingsList } = lazyImport(
  () => import('features/satisfactionRatings'),
  'SatisfactionRatingsList',
);

const App = () => (
  <Suspense fallback={<Spinner />}>
    <Outlet />
  </Suspense>
);

// 外部管理者スコープごとのホーム画面
const EXTERNAL_ADMIN_HOME = {
  [EXTERNAL_ADMIN_SCOPE.PRODUCTS]: PRODUCT_LIST_VIEW,
  [EXTERNAL_ADMIN_SCOPE.ORDERS]: ORDER_LIST_VIEW,
  [EXTERNAL_ADMIN_SCOPE.BUYER]: ORDER_LIST_VIEW,
};

// @todo Phase2: role ベースの権限制御に変更
// ルート "/"（routes/index.jsx）からも使うため named export にしている。
// 直接 <Dashboard /> を描画すると、外部管理者のログイン直後に一瞬ダッシュボードがマウントされ、
// レポートAPI5本が 403 で失敗する（外部管理者は reports 権限を持たない）
export const DashboardOrRedirect = () => {
  const { user } = useAuth();
  const home = EXTERNAL_ADMIN_HOME[user?.externalAdminScope];
  if (home) {
    return <Navigate to={home} replace />;
  }

  return <Dashboard />;
};

/**
 * 外部管理者のルートガード（旧 KaisyoGuard の一般化）
 * - 社内 admin（scope なし）は全ルート通過
 * - 外部管理者は allow に一致するスコープのルートのみ通過し、
 *   それ以外は自スコープのホーム（products→商品一覧 / orders→注文一覧）へリダイレクト
 * @todo Phase2: role ベースの権限制御に変更
 */
export const ExternalAdminGuard = ({ allow }) => {
  const { user } = useAuth();
  const scope = user?.externalAdminScope;
  if (scope && !allow.includes(scope)) {
    return <Navigate to={EXTERNAL_ADMIN_HOME[scope] ?? PRODUCT_LIST_VIEW} replace />;
  }

  return <Outlet />;
};

ExternalAdminGuard.propTypes = {
  allow: PropTypes.arrayOf(
    PropTypes.oneOf([
      EXTERNAL_ADMIN_SCOPE.PRODUCTS,
      EXTERNAL_ADMIN_SCOPE.ORDERS,
      EXTERNAL_ADMIN_SCOPE.BUYER,
    ]),
  ),
};

// 社内管理者専用ルート（外部管理者は全員リダイレクト）
ExternalAdminGuard.defaultProps = {
  allow: [],
};

const protectedRoutes = [
  {
    path: 'app',
    element: <App />,
    children: [
      { path: 'dashboard', element: <DashboardOrRedirect /> },
      // 商品管理（scope='products' の外部管理者＝カイショー社もアクセス可）
      {
        element: <ExternalAdminGuard allow={[EXTERNAL_ADMIN_SCOPE.PRODUCTS]} />,
        children: [
          {
            path: 'products',
            element: <ProductList />,
          },
          // non_rebuiltとなっているが全てのケースでこのコンポーネントを使う
          {
            path: 'products/non_rebuilt/:productId',
            element: <NonRebuiltProductDetail />,
          },
          {
            path: 'products/bulkUpdate',
            element: <ProductBulkUpdate />,
          },
          {
            path: 'products/create',
            element: <NonRebuiltProductDetail />,
          },
        ],
      },
      // 注文一覧（一城＝orders・IDOM＝buyer もアクセス可）
      {
        element: (
          <ExternalAdminGuard allow={[EXTERNAL_ADMIN_SCOPE.ORDERS, EXTERNAL_ADMIN_SCOPE.BUYER]} />
        ),
        children: [{ path: 'orders', element: <OrdersList /> }],
      },
      // 注文詳細（購入者には当社の仕入値・社内メモが出るため buyer は入れない）
      {
        element: <ExternalAdminGuard allow={[EXTERNAL_ADMIN_SCOPE.ORDERS]} />,
        children: [{ path: 'orders/:orderId', element: <OrderDetail /> }],
      },
      // @todo Phase2: role ベースの権限制御に変更（社内 admin 専用ルートを一括ガード）
      {
        element: <ExternalAdminGuard />,
        children: [
          { path: 'users', element: <UserList /> },
          { path: 'createUsers', element: <CreateUser /> },
          { path: 'users/:userId/:userType', element: <UserDetail /> },
          { path: 'inquiries', element: <InquiryList /> },
          { path: 'inquiries/:inquiryId', element: <InquiryDetail /> },
          { path: 'requests', element: <RequestsList /> },
          { path: 'requests/:requestId', element: <RequestDetail /> },
          { path: 'payments', element: <PaymentsList /> },
          { path: 'payments/:paymentId', element: <PaymentDetail /> },
          { path: 'exhibits', element: <ExhibitsList /> },
          { path: 'exhibits/create', element: <ExhibitDetail /> },
          { path: 'exhibits/:exhibitId', element: <ExhibitDetail /> },
          { path: 'administrators', element: <AdministratorList /> },
          { path: 'administrators/:administratorId', element: <AdministratorDetail /> },
          { path: 'comments', element: <CommentsList /> },
          { path: 'comments/:itemId', element: <CommentDetail /> },
          { path: 'atrsImportErrors', element: <ATRSImportErrorList /> },
          { path: 'productsMaster', element: <ProductsMaster /> },
          { path: 'importVehicleMaster', element: <ImportVehicleMaster /> },
          { path: 'withdrawals', element: <WithdrawalList /> },
          { path: 'unapprovedUsers', element: <UnapprovedUserList /> },
          { path: 'satisfactionRatings', element: <SatisfactionRatingsList /> },
        ],
      },
      { path: '', element: <DashboardOrRedirect /> },
      { path: '*', element: <Navigate to="." /> },
    ],
  },
];

export default protectedRoutes;
